import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  onAuthStateChanged,
  User,
  signOut as firebaseSignOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
  linkWithCredential,
  AuthCredential,
} from '@firebase/auth';
import { auth } from '../services/firebase';

// Result type for Google Sign-In with account linking support
export interface GoogleSignInResult {
  success: boolean;
  needsLinking?: boolean;
  email?: string;
  pendingCredential?: AuthCredential;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signOut: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signInWithGoogle: () => Promise<GoogleSignInResult>;
  linkEmailAccount: (email: string, password: string, pendingCredential: AuthCredential) => Promise<void>;
  updateDisplayName: (displayName: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  signOut: async () => {},
  signIn: async () => {},
  signUp: async () => {},
  signInWithGoogle: async () => ({ success: false }),
  linkEmailAccount: async () => {},
  updateDisplayName: async () => {},
});

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signOut = async () => {
    await firebaseSignOut(auth);
  };

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const signUp = async (email: string, password: string, displayName: string) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    if (userCredential.user) {
      await updateProfile(userCredential.user, { displayName });
    }
  };

  const signInWithGoogle = async (): Promise<GoogleSignInResult> => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      return { success: true };
    } catch (error: unknown) {
      // Check for account-exists-with-different-credential error
      if (error && typeof error === 'object' && 'code' in error) {
        const firebaseError = error as {
          code: string;
          customData?: { email?: string };
        };

        if (firebaseError.code === 'auth/account-exists-with-different-credential') {
          const email = firebaseError.customData?.email;
          // Cast to unknown first to satisfy TypeScript
          const credential = GoogleAuthProvider.credentialFromError(error as unknown as Parameters<typeof GoogleAuthProvider.credentialFromError>[0]);

          if (email && credential) {
            return {
              success: false,
              needsLinking: true,
              email,
              pendingCredential: credential,
            };
          }
        }
      }
      // Re-throw other errors
      throw error;
    }
  };

  const linkEmailAccount = async (
    email: string,
    password: string,
    pendingCredential: AuthCredential
  ): Promise<void> => {
    // 1. Sign in with email+password
    const userCredential = await signInWithEmailAndPassword(auth, email, password);

    // 2. Link the Google credential to this account
    await linkWithCredential(userCredential.user, pendingCredential);
  };

  const updateDisplayName = async (displayName: string) => {
    if (auth.currentUser) {
      await updateProfile(auth.currentUser, { displayName });
      // Force refresh user state
      setUser({ ...auth.currentUser });
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, signOut, signIn, signUp, signInWithGoogle, linkEmailAccount, updateDisplayName }}>
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext };
